import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Check, CheckSquare, Square, Search } from "lucide-react";

export interface FilterOption {
  _id: string;
  code?: string;
  name: string;
  classification?: string;
}

interface MultiSelectFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (selectedIds: string[]) => void;
  title: string;
  options: FilterOption[];
  selectedIds: string[];
  loading?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onScrollEnd?: () => void;
  hasMore?: boolean;
  single?: boolean;
}

const MultiSelectFilterModal: React.FC<MultiSelectFilterModalProps> = ({
  isOpen,
  onClose,
  onApply,
  title,
  options,
  selectedIds,
  loading = false,
  searchValue,
  onSearchChange,
  onScrollEnd,
  hasMore = false,
  single = false,
}) => {
  const { t } = useTranslation();
  const [tempSelectedIds, setTempSelectedIds] = useState<string[]>(selectedIds);
  // Index of the keyboard-highlighted option (arrow-key navigation).
  const [activeIndex, setActiveIndex] = useState(-1);
  const optionRefs = useRef<(HTMLLabelElement | null)[]>([]);

  // Sync tempSelectedIds when selectedIds prop changes
  useEffect(() => {
    if (isOpen) {
      setTempSelectedIds(selectedIds);
      setActiveIndex(0);
    }
  }, [isOpen, selectedIds]);

  // Keep the highlighted row scrolled into view; auto-load more near the end.
  useEffect(() => {
    if (activeIndex < 0) return;
    optionRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
    if (onScrollEnd && hasMore && !loading && activeIndex >= options.length - 3) {
      onScrollEnd();
    }
  }, [activeIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleApply();
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min((i < 0 ? -1 : i) + 1, options.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max((i <= 0 ? 1 : i) - 1, 0));
        return;
      }
      if (e.key === " ") {
        e.preventDefault();
        const option = options[activeIndex];
        if (option) handleToggle(option._id);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, tempSelectedIds, activeIndex, options, hasMore, loading]);

  const handleApply = () => {
    onApply(tempSelectedIds);
    onClose();
  };

  const handleToggle = (id: string) => {
    if (single) {
      setTempSelectedIds((prev) => (prev.includes(id) ? [] : [id]));
      return;
    }
    setTempSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    setTempSelectedIds(prev => Array.from(new Set([...prev, ...options.map(o => o._id)])));
  };

  const handleDeselectAll = () => {
    setTempSelectedIds([]);
  };

  const isAllSelected = options.length > 0 && options.every(opt => tempSelectedIds.includes(opt._id));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center backdrop-blur-sm z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Controls */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex-shrink-0">
          {onSearchChange && (
            <div className="relative mb-3">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={searchValue || ""}
                onChange={(e) => { onSearchChange(e.target.value); setActiveIndex(0); }}
                placeholder={t("common.search", "Search")}
                className="h-10 w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 text-sm text-gray-800 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              />
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
            {!single && (
              <button
                onClick={isAllSelected ? handleDeselectAll : handleSelectAll}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-teal-700 hover:bg-teal-50 rounded-md transition-colors"
              >
                {isAllSelected ? (
                  <>
                    <Square className="w-4 h-4" />
                    {t("common.deselectAll") || "Deselect All"}
                  </>
                ) : (
                  <>
                    <CheckSquare className="w-4 h-4" />
                    {t("common.selectAll") || "Select All"}
                  </>
                )}
              </button>
            )}
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-semibold text-teal-700">{tempSelectedIds.length}</span>
              {" "}{t("common.of") || "of"}{" "}
              <span className="font-semibold">{options.length}</span>
              {" "}{t("common.selected") || "selected"}
            </div>
          </div>
        </div>

        {/* Options List - Scrollable Area */}
        <div
          className="flex-1 overflow-y-auto px-6 py-4 min-h-0"
          onScroll={(e) => {
            if (!onScrollEnd || loading || !hasMore) return;
            const el = e.currentTarget;
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
              onScrollEnd();
            }
          }}
        >
          {loading && options.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
          ) : options.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {t("common.noOptionsAvailable") || "No options available"}
            </div>
          ) : (
            <div className="space-y-1.5">
              {options.map((option, index) => {
                const isSelected = tempSelectedIds.includes(option._id);
                const isActive = index === activeIndex;
                return (
                  <label
                    key={option._id}
                    ref={(el) => { optionRefs.current[index] = el; }}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all ${
                      isSelected
                        ? "bg-teal-50 border-2 border-teal-500"
                        : isActive
                        ? "bg-gray-50 border-2 border-teal-300"
                        : "bg-white border-2 border-transparent hover:bg-gray-50 hover:border-gray-200"
                    }`}
                  >
                    <input
                      type={single ? "radio" : "checkbox"}
                      checked={isSelected}
                      onChange={() => handleToggle(option._id)}
                      className={`w-5 h-5 text-teal-600 border-gray-300 focus:ring-teal-500 focus:ring-2 cursor-pointer flex-shrink-0 ${single ? "" : "rounded"}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {option.code && (
                          <span className="font-mono text-sm font-semibold text-gray-900">
                            {option.code}
                          </span>
                        )}
                        {option.classification && (
                          <span className="font-mono text-sm font-semibold text-gray-900">
                            {option.classification}
                          </span>
                        )}
                        <span className="text-sm text-gray-700 break-words">
                          {option.name}
                        </span>
                      </div>
                    </div>
                    {isSelected && (
                      <Check className="w-5 h-5 text-teal-600 flex-shrink-0" />
                    )}
                  </label>
                );
              })}
              {loading && hasMore && (
                <div className="flex justify-center py-3">
                  <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-teal-600" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t("common.cancel") || "Cancel"}
          </button>
          <button
            onClick={handleApply}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2"
          >
            <Check className="w-4 h-4" />
            {t("common.apply") || "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiSelectFilterModal;
